// src/components/LabEditor.jsx
// Éditeur visuel de lab Cisco pour AdminQuestions
import React, { useState } from "react";
import {
  Box, Stack, Typography, TextField, Button, Chip, Paper,
  Select, MenuItem, FormControl, InputLabel, IconButton,
  Divider, Alert, Tabs, Tab, alpha,
} from "@mui/material";
import AddIcon           from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AccountTreeIcon   from "@mui/icons-material/AccountTree";
import TaskAltIcon       from "@mui/icons-material/TaskAlt";
import InfoIcon          from "@mui/icons-material/Info";
import LabTopology       from "./LabTopology";
import { P }             from "../theme";

const DEVICE_TYPES  = ["router", "switch", "pc", "server", "cloud", "firewall"];
const CHECK_TYPES   = [
  { value: "ip_configured",   label: "IP configurée",           fields: ["expected_ip", "device"] },
  { value: "interface_up",    label: "Interface active",         fields: ["interface", "device"] },
  { value: "ospf_configured", label: "OSPF configuré",           fields: ["device"] },
  { value: "ospf_network",    label: "Réseau OSPF présent",      fields: ["expected_network", "device"] },
  { value: "hostname",        label: "Hostname correct",         fields: ["expected_hostname", "device"] },
  { value: "static_route",    label: "Route statique",           fields: ["expected_network", "device"] },
  { value: "rip_configured",  label: "RIP configuré",            fields: ["device"] },
  { value: "enable_password", label: "Enable password",          fields: ["expected_password", "device"] },
];

const EMPTY_DEVICE = { id: "R1", type: "router", x: 100, y: 100, label: "" };
const EMPTY_LINK   = { from: "", to: "", label: "" };
const EMPTY_TASK   = { id: "", desc: "", device: "", check: "ip_configured",
                       expected_ip: "", expected_network: "", expected_hostname: "",
                       expected_password: "", interface: "" };

export default function LabEditor({ value = {}, onChange }) {
  const [tab, setTab] = useState(0);
  const topo    = value.topology  || { devices: [], links: [] };
  const tasks   = value.tasks     || [];
  const scenario = value.scenario || "";

  const update = (patch) => onChange({ ...value, ...patch });
  const updateTopo = (patch) => update({ topology: { ...topo, ...patch } });

  // ── Devices ───────────────────────────────────────────────────────────────
  const addDevice = () => {
    const newId = `D${topo.devices.length + 1}`;
    updateTopo({ devices: [...topo.devices, { ...EMPTY_DEVICE, id: newId }] });
  };
  const updateDevice = (i, patch) => {
    const devices = topo.devices.map((d, j) => j === i ? { ...d, ...patch } : d);
    updateTopo({ devices });
  };
  // LabTopology travaille par id d'appareil (cohérent avec onSelectDevice),
  // alors que updateDevice() ci-dessus attend un index — petit adaptateur.
  const moveDeviceById = (devId, { x, y }) => {
    const i = topo.devices.findIndex(d => d.id === devId);
    if (i !== -1) updateDevice(i, { x, y });
  };
  const removeDevice = (i) => {
    const removed = topo.devices[i].id;
    updateTopo({
      devices: topo.devices.filter((_, j) => j !== i),
      links:   topo.links.filter(l => l.from !== removed && l.to !== removed),
    });
  };

  // ── Links ─────────────────────────────────────────────────────────────────
  const addLink = () => updateTopo({ links: [...topo.links, { ...EMPTY_LINK }] });
  const updateLink = (i, patch) => {
    updateTopo({ links: topo.links.map((l, j) => j === i ? { ...l, ...patch } : l) });
  };
  const removeLink = (i) => updateTopo({ links: topo.links.filter((_, j) => j !== i) });

  // ── Tasks ─────────────────────────────────────────────────────────────────
  const addTask = () => {
    const id = `t${tasks.length + 1}`;
    update({ tasks: [...tasks, { ...EMPTY_TASK, id }] });
  };
  const updateTask = (i, patch) => {
    update({ tasks: tasks.map((t, j) => j === i ? { ...t, ...patch } : t) });
  };
  const removeTask = (i) => update({ tasks: tasks.filter((_, j) => j !== i) });

  const deviceIds = topo.devices.map(d => d.id);

  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: `1px solid ${P.slate[200]}` }}>
        <Tab icon={<InfoIcon fontSize="small" />}       iconPosition="start" label="Scénario" />
        <Tab icon={<AccountTreeIcon fontSize="small" />} iconPosition="start" label="Topologie" />
        <Tab icon={<TaskAltIcon fontSize="small" />}    iconPosition="start" label="Tâches" />
      </Tabs>

      {/* ── Onglet 0 : Scénario ─────────────────────────────────────────── */}
      {tab === 0 && (
        <Stack spacing={2}>
          <Alert severity="info" sx={{ borderRadius: "8px" }}>
            Le scénario est affiché à l'étudiant avant qu'il commence la configuration.
            Décrivez le contexte réseau et les objectifs.
          </Alert>
          <TextField
            label="Description du scénario"
            multiline minRows={4}
            fullWidth
            value={scenario}
            onChange={e => update({ scenario: e.target.value })}
            placeholder={`Exemple :\nVous êtes l'administrateur réseau d'une PME.\nConfigurez R1 avec l'adresse IP 192.168.1.1/24 sur l'interface Gi0/0\net activez OSPF avec le process-id 1.`}
          />
        </Stack>
      )}

      {/* ── Onglet 1 : Topologie ─────────────────────────────────────────── */}
      {tab === 1 && (
        <Stack spacing={2}>
          {/* Prévisualisation */}
          {topo.devices.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: .5, display: "block", fontWeight: 600 }}>
                Prévisualisation — glissez un équipement pour le repositionner
              </Typography>
              <LabTopology
                devices={topo.devices}
                links={topo.links}
                height={200}
                onSelectDevice={null}
                onDeviceMove={moveDeviceById}
              />
            </Box>
          )}

          {/* Équipements */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>Équipements</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addDevice}>Ajouter</Button>
            </Stack>
            <Stack spacing={1.5}>
              {topo.devices.map((d, i) => (
                <Paper key={i} sx={{ p: 1.5, border: `1px solid ${P.slate[200]}` }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                    <TextField size="small" label="ID" value={d.id}
                      onChange={e => updateDevice(i, { id: e.target.value })} sx={{ width: 80 }} />
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Type</InputLabel>
                      <Select value={d.type} label="Type" onChange={e => updateDevice(i, { type: e.target.value })}>
                        {DEVICE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField size="small" label="Label" value={d.label || ""}
                      onChange={e => updateDevice(i, { label: e.target.value })} sx={{ flex: 1 }} />
                    <Stack direction="row" spacing={1}>
                      <TextField size="small" label="X" type="number" value={d.x}
                        onChange={e => updateDevice(i, { x: Number(e.target.value) })} sx={{ width: 70 }} />
                      <TextField size="small" label="Y" type="number" value={d.y}
                        onChange={e => updateDevice(i, { y: Number(e.target.value) })} sx={{ width: 70 }} />
                    </Stack>
                    <IconButton size="small" onClick={() => removeDevice(i)} sx={{ color: P.red }}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
              {topo.devices.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                  Aucun équipement. Cliquez sur "Ajouter".
                </Typography>
              )}
            </Stack>
          </Box>

          <Divider />

          {/* Liens */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>Liens</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addLink}>Ajouter</Button>
            </Stack>
            <Stack spacing={1}>
              {topo.links.map((l, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center">
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>De</InputLabel>
                    <Select value={l.from} label="De" onChange={e => updateLink(i, { from: e.target.value })}>
                      {deviceIds.map(id => <MenuItem key={id} value={id}>{id}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Typography sx={{ color: P.slate[400] }}>→</Typography>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Vers</InputLabel>
                    <Select value={l.to} label="Vers" onChange={e => updateLink(i, { to: e.target.value })}>
                      {deviceIds.map(id => <MenuItem key={id} value={id}>{id}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField size="small" label="Label" value={l.label || ""}
                    onChange={e => updateLink(i, { label: e.target.value })} sx={{ width: 100 }} />
                  <IconButton size="small" onClick={() => removeLink(i)} sx={{ color: P.red }}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Stack>
      )}

      {/* ── Onglet 2 : Tâches ─────────────────────────────────────────────── */}
      {tab === 2 && (
        <Stack spacing={2}>
          <Alert severity="info" sx={{ borderRadius: "8px" }}>
            Chaque tâche est vérifiée automatiquement selon l'état du terminal.
            La validation est côté serveur.
          </Alert>
          {tasks.map((t, i) => {
            const checkDef = CHECK_TYPES.find(c => c.value === t.check) || CHECK_TYPES[0];
            return (
              <Paper key={i} sx={{ p: 2, border: `1px solid ${P.slate[200]}` }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Chip label={`Tâche ${i + 1}`} size="small"
                    sx={{ fontWeight: 700, background: alpha(P.teal, .1), color: P.teal }} />
                  <IconButton size="small" onClick={() => removeTask(i)} sx={{ color: P.red }}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
                <Stack spacing={1.5}>
                  <TextField size="small" label="Description de la tâche *" fullWidth
                    value={t.desc} onChange={e => updateTask(i, { desc: e.target.value })}
                    placeholder="Ex: Configurez l'IP 192.168.1.1/24 sur R1" />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Équipement cible *</InputLabel>
                      <Select value={t.device} label="Équipement cible *"
                        onChange={e => updateTask(i, { device: e.target.value })}>
                        {deviceIds.map(id => <MenuItem key={id} value={id}>{id}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Type de vérification *</InputLabel>
                      <Select value={t.check} label="Type de vérification *"
                        onChange={e => updateTask(i, { check: e.target.value })}>
                        {CHECK_TYPES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Stack>
                  {/* Champs conditionnels */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap">
                    {checkDef.fields.includes("expected_ip") && (
                      <TextField size="small" label="IP attendue" value={t.expected_ip || ""}
                        onChange={e => updateTask(i, { expected_ip: e.target.value })}
                        placeholder="192.168.1.1" sx={{ flex: 1 }} />
                    )}
                    {checkDef.fields.includes("expected_network") && (
                      <TextField size="small" label="Réseau attendu" value={t.expected_network || ""}
                        onChange={e => updateTask(i, { expected_network: e.target.value })}
                        placeholder="192.168.1.0" sx={{ flex: 1 }} />
                    )}
                    {checkDef.fields.includes("expected_hostname") && (
                      <TextField size="small" label="Hostname attendu" value={t.expected_hostname || ""}
                        onChange={e => updateTask(i, { expected_hostname: e.target.value })}
                        placeholder="R1-Core" sx={{ flex: 1 }} />
                    )}
                    {checkDef.fields.includes("expected_password") && (
                      <TextField size="small" label="Mot de passe attendu" value={t.expected_password || ""}
                        onChange={e => updateTask(i, { expected_password: e.target.value })}
                        placeholder="cisco" sx={{ flex: 1 }} />
                    )}
                    {checkDef.fields.includes("interface") && (
                      <TextField size="small" label="Interface" value={t.interface || ""}
                        onChange={e => updateTask(i, { interface: e.target.value })}
                        placeholder="GigabitEthernet0/0" sx={{ flex: 1 }} />
                    )}
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addTask}
            disabled={deviceIds.length === 0}
            sx={{ alignSelf: "flex-start" }}>
            Ajouter une tâche
          </Button>
          {deviceIds.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              Ajoutez d'abord des équipements dans l'onglet Topologie.
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}